import { Component, effect, inject, Injector, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';

import { AuthService } from '@ideals/core/auth';
import { ClarityService } from '@ideals/core/clarity';
import { GoogleAnalyticsService } from '@ideals/core/google-analytics';
import { IntercomService } from '@ideals/core/intercom';
import { LogRocketService } from '@ideals/core/logrocket';
import { SentryService } from '@ideals/core/sentry';
import { IndexedDbService } from '@ideals/services/indexed-db';
import { LocalStorageService } from '@ideals/services/local-storage';
import { getUserDisplayName } from '@ideals/utils/get-user-display-name';

import { HIDE_ADVISORY_SUITE_BANNER_KEY, HIDE_ADVISORY_SUITE_NOTIFICATION_KEY } from '@vdr/domains/advisory-suite/models';

import { DataTransporterService } from './data/services/data-transporter';
import { OperationTrackerService } from './data/services/operation-tracker';
import { getPermissionsUpdateCountKey } from './data/utils/get-permissions-update-count-key';
import { HIDE_CONTRACT_EXPIRES_NOTIFICATION_KEY, HIDE_OUTSTANDING_BALANCE_NOTIFICATION_KEY } from './domains/corporate-account/models';
import { HIDE_DOCUMENTS_BULK_RENAME_TRIAL_PROMOTION_KEY } from './domains/documents-bulk-rename/models';
import { AuthUserStore } from './domains/profile/store/auth-user';
import { HIDE_MIGRATED_SHARED_LINK_INFO_KEY } from './domains/shared-links/models';
import { environment } from './environments/environment';

@Component({
  host: {
    class: 'ideals-app-root',
  },
  imports: [
    RouterOutlet,
  ],
  selector: 'vdr-root',
  templateUrl: './root.container.html',
})
export class RootContainer implements OnInit {
  readonly #authService = inject(AuthService);
  readonly #authUserStore = inject(AuthUserStore);
  readonly #clarityService = inject(ClarityService);
  readonly #dataTransporterService = inject(DataTransporterService);
  readonly #googleAnalyticsService = inject(GoogleAnalyticsService);
  readonly #indexedDbService = inject(IndexedDbService);
  readonly #injector = inject(Injector);
  readonly #intercomService = inject(IntercomService);
  readonly #localStorageService = inject(LocalStorageService);
  readonly #logRocketService = inject(LogRocketService);
  readonly #operationTrackerService = inject(OperationTrackerService);
  readonly #sentryService = inject(SentryService);

  ngOnInit(): void {
    if (this.#authService.isLoggedIn && !this.#authService.hasRole('guest')) {
      this.#authUserStore.loadAuthUser();
    }

    this.#authService.events$
      .pipe(filter((event) => ['logout', 'session_error'].includes(event.type)))
      .subscribe(() => {
        this.#indexedDbService.clear();
        this.#localStorageService.removeItem(HIDE_CONTRACT_EXPIRES_NOTIFICATION_KEY);
        this.#localStorageService.removeItem(HIDE_OUTSTANDING_BALANCE_NOTIFICATION_KEY);
        this.#localStorageService.removeItem(HIDE_DOCUMENTS_BULK_RENAME_TRIAL_PROMOTION_KEY);
        this.#localStorageService.removeItem(HIDE_MIGRATED_SHARED_LINK_INFO_KEY);
        this.#localStorageService.removeItem(HIDE_ADVISORY_SUITE_BANNER_KEY);
        this.#localStorageService.removeItem(HIDE_ADVISORY_SUITE_NOTIFICATION_KEY);
        this.#operationTrackerService.reset('vdr-documents-operations-count');
        this.#sentryService.reset();

        if (this.#intercomService.intercom) {
          this.#intercomService.shutdown();
        }
      });

    this.#authService.startIdleTracking();

    if (environment.intercomEnabled) {
      this.#intercomService.initialize();
    }

    this.#effects();
  }

  #effects(): void {
    effect(() => {
      const authUser = this.#authUserStore.user();

      if (authUser) {
        this.#dataTransporterService.setUser(authUser);
        this.#clarityService.identify(authUser.id);
        this.#googleAnalyticsService.identify(authUser.id);
        this.#operationTrackerService.setup({
          'vdr-documents-operations-count': 'number_of_move_copy_restore_operations',
          [getPermissionsUpdateCountKey(authUser.id)]: 'apply_permission_count_admin',
        });
        this.#logRocketService.identify({
          email: authUser.email,
          id: authUser.id,
          name: getUserDisplayName(authUser),
        });
        this.#sentryService.identify({
          email: authUser.email,
          id: authUser.id,
          username: getUserDisplayName(authUser),
        });
      }
    }, { injector: this.#injector });
  }
}
